import { useMediaQuery } from '@mui/material';
import Grid from '@mui/material/Grid';
import LabelledDropdown from 'components/atoms/Dropdown/LabelledDropdown';
import { filter, keys, map } from 'es-toolkit/compat';
import { MainPageFilterKey } from 'interfaces/cohort';
import { Permission } from 'interfaces/permissionOption';
import React from 'react';
import { Approval } from 'services/filters';
import { BooleanParam, JsonParam, StringParam, useQueryParams } from 'use-query-params';
import { usePermissions } from 'utils/usePermissions';
import useMainFilters from '../hooks/useMainFilters';
import MainFilter from './MainFilter';

// all main filters except for approvement/status because this one is shown in its own grid according to permission
const mainFilterKeys = keys(MainPageFilterKey);

const labelMap = {
  approvement: 'Status',
  studyId: 'Study',
  cancerTypes: 'Indication',
  stains: 'Stain',
  cancerSubtypes: 'Sub Indication',
  orchestrationId: 'Orchestration ID',
};

const wideFilterKeys = new Set(['cancerTypes', 'cancerSubtypes']);
const COMPACT_FILTER_LAYOUT_MAX_WIDTH = 1800;
const DEFAULT_WIDE_FILTER_MIN_WIDTH = 210;
const COMPACT_FILTER_MIN_WIDTH = 140;

export const getMainFiltersSpacing = ({
  isDrawer,
  compactLaptopFilters,
}: {
  isDrawer?: boolean;
  compactLaptopFilters: boolean;
}) => {
  if (isDrawer) {
    return 2;
  }

  return compactLaptopFilters ? 0.5 : 0.75;
};

export const getMainFiltersBaseUnits = ({
  filterCount,
  isDrawer,
  compactLaptopFilters,
}: {
  filterCount: number;
  isDrawer?: boolean;
  compactLaptopFilters: boolean;
}) => {
  if (isDrawer) {
    return 12;
  }

  return 12 / (filterCount + (compactLaptopFilters ? 0 : 1));
};

export const getMainFilterGridUnits = ({
  filterKey,
  baseUnits,
  compactLaptopFilters,
}: {
  filterKey: string;
  baseUnits: number;
  compactLaptopFilters: boolean;
}) => {
  if (filterKey === MainPageFilterKey.searchTerm && !compactLaptopFilters) {
    return baseUnits * 2;
  }

  return baseUnits;
};

export const getMainFilterMinWidth = ({
  filterKey,
  isDrawer,
  compactLaptopFilters,
}: {
  filterKey: string;
  isDrawer?: boolean;
  compactLaptopFilters: boolean;
}) => {
  if (isDrawer) {
    return undefined;
  }

  if (filterKey === MainPageFilterKey.searchTerm) {
    return compactLaptopFilters ? 150 : undefined;
  }

  if (wideFilterKeys.has(filterKey)) {
    return compactLaptopFilters ? COMPACT_FILTER_MIN_WIDTH : DEFAULT_WIDE_FILTER_MIN_WIDTH;
  }

  if (filterKey === MainPageFilterKey.orchestrationId) {
    return compactLaptopFilters ? COMPACT_FILTER_MIN_WIDTH : 180;
  }

  return undefined;
};

export interface ProcedureFilterProps {
  showApprovedFilter: boolean;
  onChangeFromAdvancedSearch?: (key: string, value: any) => void;
  isDrawer?: boolean;
  controlledValues?: Record<string, any>;
  shouldAllowSaveCohort?: boolean;
  enableQueries?: boolean;
}

const MainFilters: React.FunctionComponent<React.PropsWithChildren<ProcedureFilterProps>> = ({
  showApprovedFilter,
  isDrawer,
  onChangeFromAdvancedSearch = null,
  controlledValues = {},
  enableQueries = true,
}) => {
  const compactLaptopFilters = useMediaQuery(`(max-width:${COMPACT_FILTER_LAYOUT_MAX_WIDTH}px)`);
  const { approvalOptions, handleFilterChange } = useMainFilters({
    enableQueries,
  });

  const handleChange = isDrawer && onChangeFromAdvancedSearch ? onChangeFromAdvancedSearch : handleFilterChange;

  const [queryParams, setQueryParams] = useQueryParams({
    cohortId: StringParam,
    queryId: StringParam,
    advancedSearchDrawerOpen: BooleanParam,
    filters: JsonParam,
    features: JsonParam,
    clinicalData: JsonParam,
    compositeFilters: JsonParam,
    resultsMode: StringParam,
    searchTerm: StringParam,
  });

  const { hasPermission } = usePermissions();
  const showOrchestrationFilter = hasPermission(Permission.ViewUnpublishedResults);
  const mainFilterKeysToShow = showOrchestrationFilter
    ? mainFilterKeys
    : filter(mainFilterKeys, (key) => key !== 'orchestrationId');

  const numberOfColumns = isDrawer ? 12 : mainFilterKeysToShow.length;
  const baseUnits = getMainFiltersBaseUnits({ filterCount: numberOfColumns, isDrawer, compactLaptopFilters });
  const xs = isDrawer ? 12 : baseUnits;
  const spacing = getMainFiltersSpacing({ isDrawer, compactLaptopFilters });

  const approval = queryParams.filters?.approvement || Approval.All;

  const handleApprovalFieldChange = (value: string) => {
    // TODO: fix 'Unapproved' to 'Disapproved' in backend and use handleChange instead of setQueryParams
    if (isDrawer && onChangeFromAdvancedSearch) {
      onChangeFromAdvancedSearch('approvement', value);
    } else {
      switch (value) {
        case Approval.Approved:
          setQueryParams({ filters: { ...queryParams.filters, approvement: 'Approved' } });
          break;
        case Approval.Disapproved:
          setQueryParams({ filters: { ...queryParams.filters, approvement: 'Unapproved' } });
          break;
        default:
          setQueryParams({ filters: { ...queryParams.filters, approvement: null } });
      }
    }
  };

  return (
    <Grid container spacing={spacing} wrap="wrap" flexDirection="column">
      <Grid container item justifyContent="space-between" spacing={spacing}>
        {showApprovedFilter && isDrawer && (
          <Grid item xs={xs}>
            <LabelledDropdown
              size="small"
              key="approvement"
              label="Status"
              value={controlledValues['approvement'] ?? approval}
              options={approvalOptions}
              onOptionSelected={handleApprovalFieldChange}
            />
          </Grid>
        )}
      </Grid>

      <Grid container item spacing={spacing} wrap="wrap" sx={{ minWidth: 0 }}>
        {map(mainFilterKeysToShow, (filterKey) => {
          const filterValue = controlledValues[filterKey] ?? queryParams?.filters?.[filterKey];
          const isWideFilter = wideFilterKeys.has(filterKey);
          const minWidth = getMainFilterMinWidth({ filterKey, isDrawer, compactLaptopFilters });

          return (
            <Grid
              item
              xs={getMainFilterGridUnits({ filterKey, baseUnits, compactLaptopFilters })}
              key={filterKey}
              sx={
                !isDrawer
                  ? {
                      minWidth: minWidth || 0,
                      flexGrow: isWideFilter || filterKey === MainPageFilterKey.searchTerm ? 1 : undefined,
                      '& .MuiInputBase-root': compactLaptopFilters
                        ? {
                            fontSize: '0.8125rem',
                          }
                        : undefined,
                      '& .MuiInputLabel-root': compactLaptopFilters
                        ? {
                            fontSize: '0.85rem',
                          }
                        : undefined,
                      '& .MuiAutocomplete-tag': compactLaptopFilters
                        ? {
                            maxWidth: 120,
                          }
                        : undefined,
                    }
                  : undefined
              }
            >
              <MainFilter
                label={labelMap[filterKey as keyof typeof labelMap]}
                filterKey={filterKey}
                filterValue={filterValue}
                onChange={handleChange}
                controlledValues={controlledValues}
                handleApprovalFieldChange={handleApprovalFieldChange}
                enableQueries={enableQueries}
                isDrawer={isDrawer}
              />
            </Grid>
          );
        })}
      </Grid>
    </Grid>
  );
};

export default MainFilters;
